import React, { useState, useRef, useEffect } from 'react';
import { OPEN_APP_ROUTES } from './LoginGate';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useTier } from '../context/TierContext';
import { TOKEN_KEY, signOut, clearUserState, isAuthenticated } from '../utils/auth';
import MarketHeatmap from './MarketHeatmap';
import TreasuryYields from './TreasuryYields';
import HeroGradeWidget from './HeroGradeWidget';
import useModelAccess from '../hooks/useModelAccess';
import './LandingPage.css';

// Card order mirrors the nav order in Navigation.js (drawerLinks, minus Home).
// Keep the two in sync if either changes.
//
// 2026-09-11: reordered by what a new user can actually do. This list used to
// lead with Trends and put Correlations - a paid (Core+) tool - second, while the
// Stock Screener, the free core of the product, sat 4th. Someone who just signed
// up met a locked card before they met the thing they came for. Free, high-value
// surfaces first; the two Premium cards last.
const pages = [
  {
    title: 'Stock Screener',
    description: 'Enter any tickers and run 34 conviction tests — quality, setup, and convex scoring with AI-powered analysis.',
    icon: (
      <svg viewBox="0 0 48 48" className="card-icon-svg">
        <circle cx="20" cy="20" r="14" fill="none" stroke="currentColor" strokeWidth="3"/>
        <line x1="30" y1="30" x2="44" y2="44" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
        <line x1="14" y1="20" x2="26" y2="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <line x1="20" y1="14" x2="20" y2="26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    path: '/screener',
    accent: 'accent-blue',
  },
  {
    title: 'Portfolio Analysis',
    description: 'Upload your holdings and get conviction scores, action recommendations, and AI chat about your positions.',
    icon: (
      <svg viewBox="0 0 48 48" className="card-icon-svg">
        <rect x="6" y="4" width="36" height="40" rx="4" fill="none" stroke="currentColor" strokeWidth="3"/>
        <line x1="14" y1="16" x2="34" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <line x1="14" y1="24" x2="28" y2="24" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <line x1="14" y1="32" x2="22" y2="32" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    path: '/portfolio',
    accent: 'accent-yellow',
  },
  {
    // 2026-10-05: the feed shipped with links only in the app nav and drawer,
    // which this page does not show, so the page he lands on after signing in
    // had no way to it. He found the strip on a grade card and asked where the
    // portfolio news lived.
    title: 'News on Your Names',
    description: 'The latest real headlines on your holdings, watchlists and screened tickers, with filler removed. Share the good ones straight to X.',
    icon: (
      <svg viewBox="0 0 48 48" className="card-icon-svg">
        <rect x="10" y="6" width="32" height="36" rx="3" fill="none" stroke="currentColor" strokeWidth="3"/>
        <path d="M10 14H6v24a4 4 0 0 0 8 0" fill="none" stroke="currentColor" strokeWidth="3" strokeLinejoin="round"/>
        <line x1="17" y1="15" x2="35" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <line x1="17" y1="23" x2="35" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <line x1="17" y1="31" x2="28" y2="31" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    path: '/news',
    accent: 'accent-cyan',
  },
  {
    // Shown only to accounts that can open it (owner-only until
    // MODEL_PORTFOLIOS_OPEN=1); see `visiblePages` below.
    title: 'Model Portfolios',
    description: 'AlphaGrade trades imaginary money on its own grades every night. Follow the house portfolio, or set up your own experiment and watch what it picks.',
    icon: (
      <svg viewBox="0 0 48 48" className="card-icon-svg">
        <path d="M18 4h12M20 4v14L8 38a3 3 0 0 0 2.6 4.5h26.8A3 3 0 0 0 40 38L28 18V4" fill="none" stroke="currentColor" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round"/>
        <line x1="13" y1="30" x2="35" y2="30" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    path: '/model-portfolios',
    accent: 'accent-green',
    requiresModelAccess: true,
  },
  {
    title: 'Market Trends',
    description: 'Real-time indices, sector performance, top movers by company size, and an earnings calendar that shows who beat or missed, and how the stock reacted.',
    icon: (
      <svg viewBox="0 0 48 48" className="card-icon-svg">
        <polyline points="4,36 14,22 22,28 34,10 44,18" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
        <polyline points="36,10 44,10 44,18" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    path: '/trends',
    accent: 'accent-green',
  },
  {
    title: 'Macro Energy',
    description: 'Real-time market regime detection from 6 indicators: liquidity, credit, breadth, volatility, sentiment and leadership.',
    icon: (
      <svg viewBox="0 0 48 48" className="card-icon-svg">
        <polygon points="24,2 30,18 48,18 34,28 38,46 24,34 10,46 14,28 0,18 18,18" fill="none" stroke="currentColor" strokeWidth="3" strokeLinejoin="round"/>
      </svg>
    ),
    path: '/energy',
    accent: 'accent-orange',
  },
  {
    title: 'Market Research',
    description: 'Find new ideas fast. Ask one question, get a list of tickers, and screen them instantly — built for quick discovery.',
    icon: (
      <svg viewBox="0 0 48 48" className="card-icon-svg">
        <path d="M18 36h12" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M20 42h8" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M24 4a14 14 0 0 0-8 25.4c1.2.8 2 2.2 2 3.6V36h12v-3a4.4 4.4 0 0 1 2-3.6A14 14 0 0 0 24 4z" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    path: '/research',
    accent: 'accent-cyan',
  },
  {
    title: 'Correlations',
    description: "See how any stock moves with the market, sectors, and macro. Cross-asset correlation views — when correlations break, opportunities open.",
    icon: (
      <svg viewBox="0 0 48 48" className="card-icon-svg">
        <rect x="6" y="6" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2.5"/>
        <rect x="19" y="6" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2.5"/>
        <rect x="32" y="6" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2.5"/>
        <rect x="6" y="19" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2.5"/>
        <rect x="19" y="19" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2.5"/>
        <rect x="32" y="19" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2.5"/>
        <rect x="6" y="32" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2.5"/>
        <rect x="19" y="32" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2.5"/>
        <rect x="32" y="32" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2.5"/>
      </svg>
    ),
    path: '/correlations',
    accent: 'accent-alpha',
    badge: 'Core+',
  },
  {
    title: 'Alpha Workspace',
    description: 'Think out loud with a research partner that remembers. Develop theses across multiple conversations — Alpha picks up where you left off.',
    icon: (
      <svg viewBox="0 0 48 48" className="card-icon-svg">
        <path d="M42 28a4 4 0 0 1-4 4H16l-8 8V12a4 4 0 0 1 4-4h26a4 4 0 0 1 4 4z" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M25 14l1.8 4.2L31 20l-3 3.1.7 4.2L25 25.5l-3.7 1.8.7-4.2-3-3.1 4.2-1.8z" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round"/>
      </svg>
    ),
    path: '/alpha',
    accent: 'accent-alpha',
    badge: 'Core+',
  },
];

const SCHEME_LABELS = { neon: 'Emerald', ocean: 'Ocean', ember: 'Ember' };
const SCHEME_COLORS = {
  neon:  ['#10B981', '#00D4AA'],
  ocean: ['#7B68EE', '#00B4D8'],
  ember: ['#F97316', '#F59E0B'],
};

// ── Formatting helpers (mirror MarketTrends.js conventions) ──
const fmtVal = (n) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
const fmtPct = (n) => `${n >= 0 ? '+' : ''}${fmtVal(n)}%`;

// The six Energy Matrix inputs surfaced in the Market Regime block — AlphaGrade's
// proprietary read, sourced from /api/macro/current (cached, cheap).
const REGIME_INPUTS = [
  { key: 'system_liquidity', label: 'Liquidity' },
  { key: 'credit_spreads', label: 'Credit' },
  { key: 'market_breadth', label: 'Breadth' },
  { key: 'vol_term_structure', label: 'Volatility' },
  { key: 'sentiment', label: 'Sentiment' },
  { key: 'leadership', label: 'Leadership' },
];

// Article thumbnail that cleanly falls back to a branded placeholder when the
// source supplies no image or the image fails to load — never an empty box.
function NewsThumb({ src }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return <span className="news-thumb news-thumb-ph" aria-hidden="true">AG</span>;
  }
  return <img className="news-thumb" src={src} alt="" loading="lazy" onError={() => setFailed(true)} />;
}

export default function LandingPage() {
  const navigate = useNavigate();
  const { theme, toggleTheme, colorScheme, setColorScheme, COLOR_SCHEMES } = useTheme();
  const { tier, name: tierName, hasFeature } = useTier();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [accountTab, setAccountTab] = useState('email');
  const [accountForm, setAccountForm] = useState({ newEmail: '', password: '', currentPassword: '', newPassword: '', confirmNewPassword: '' });
  const [accountError, setAccountError] = useState(null);
  const [accountSuccess, setAccountSuccess] = useState(null);
  const [accountSubmitting, setAccountSubmitting] = useState(false);
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const menuRef = useRef(null);

  // ── Live market data for the content-forward home ──
  const [trendData, setTrendData] = useState(null);
  const [trendsLoading, setTrendsLoading] = useState(true);
  const [trendsError, setTrendsError] = useState(false);
  // Proprietary market-regime read (Energy Matrix) and the latest news feed.
  const [regime, setRegime] = useState(null);
  const [regimeLoading, setRegimeLoading] = useState(true);
  const [news, setNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);
  // Proprietary "highest-conviction names" carrot (tier-aware from the server).
  const [spotlight, setSpotlight] = useState(null);
  // First-visit orientation banner (dismissed forever via localStorage).
  const [showIntro, setShowIntro] = useState(() => {
    try { return !localStorage.getItem('ag_home_intro_seen'); } catch { return false; }
  });

  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setSettingsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Fetch the same market snapshot the Trends page uses. Degrades gracefully:
  // on failure the home still renders (hero + tools), just without live blocks.
  useEffect(() => {
    let cancelled = false;
    fetch('/api/market/trends')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('bad status'))))
      .then((d) => { if (!cancelled) { setTrendData(d); setTrendsLoading(false); } })
      .catch(() => { if (!cancelled) { setTrendsError(true); setTrendsLoading(false); } });

    // Proprietary regime + news — each degrades independently.
    fetch('/api/macro/current')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('bad status'))))
      .then((d) => { if (!cancelled) { setRegime(d); setRegimeLoading(false); } })
      .catch(() => { if (!cancelled) setRegimeLoading(false); });

    fetch('/api/market/news?limit=9')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('bad status'))))
      .then((d) => { if (!cancelled) { setNews((d.articles || []).slice(0, 9)); setNewsLoading(false); } })
      .catch(() => { if (!cancelled) setNewsLoading(false); });

    // Conviction spotlight: tier gating is server-side; logged out = the free view.
    const token = localStorage.getItem(TOKEN_KEY);
    fetch('/api/market/conviction-spotlight', { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('bad status'))))
      .then((d) => { if (!cancelled) setSpotlight(d); })
      .catch(() => {});

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (showAccountModal && !userEmail) {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        fetch('/api/auth/me', { headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.ok ? r.json() : null)
          .then(data => { if (data) setUserEmail(data.email); })
          .catch(() => {});
      }
    }
  }, [showAccountModal, userEmail]);

  const dismissIntro = () => {
    try { localStorage.setItem('ag_home_intro_seen', '1'); } catch {}
    setShowIntro(false);
  };

  const handleAccountSubmit = async (e) => {
    e.preventDefault();
    setAccountError(null);
    setAccountSuccess(null);
    setAccountSubmitting(true);
    const token = localStorage.getItem(TOKEN_KEY);
    try {
      if (accountTab === 'email') {
        const resp = await fetch('/api/auth/update-email', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ new_email: accountForm.newEmail, password: accountForm.password }),
        });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.detail || 'Failed to update email');
        setUserEmail(data.email);
        setAccountSuccess('Email updated successfully');
        setAccountForm(f => ({ ...f, newEmail: '', password: '' }));
      } else if (accountTab === 'password') {
        if (accountForm.newPassword !== accountForm.confirmNewPassword) {
          throw new Error('New passwords do not match');
        }
        const resp = await fetch('/api/auth/update-password', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ current_password: accountForm.currentPassword, new_password: accountForm.newPassword }),
        });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.detail || 'Failed to update password');
        setAccountSuccess('Password updated successfully');
        setAccountForm(f => ({ ...f, currentPassword: '', newPassword: '', confirmNewPassword: '' }));
      } else if (accountTab === 'delete') {
        const resp = await fetch('/api/auth/delete-account', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ password: accountForm.currentPassword }),
        });
        if (!resp.ok) {
          const data = await resp.json().catch(() => ({}));
          throw new Error(data.detail || 'Failed to delete account');
        }
        clearUserState();
        signOut(); // removes token + redirects to '/'
        return;
      }
    } catch (err) {
      setAccountError(err.message);
    } finally {
      setAccountSubmitting(false);
    }
  };

  // Derived live-data slices for the home blocks.
  const indices = Object.entries(trendData?.marketSummary || {});
  const gainers = (trendData?.topMovers?.large_cap_gainers || []).filter(s => s.symbol !== 'N/A').slice(0, 8);
  const losers = (trendData?.topMovers?.large_cap_losers || []).filter(s => s.symbol !== 'N/A').slice(0, 8);
  const fearGreed = trendData?.fearGreedIndex;
  const sectors = (trendData?.sectorPerformance || []).slice(0, 12);

  // Map the Energy Matrix score (0-5) to a plain-English regime label + tone.
  const regimeScore = regime?.energy_matrix_score ?? null;
  // The label comes from the engine (/api/macro/current `regime`). This page
  // used its own 2.1 / 3.4 cutoffs against the engine's 1.75 / 3.25, so the
  // same day could read differently here and on the regime page. The local
  // fallback mirrors energy_matrix.regime_label for an old cached response.
  const regimeLabel = regimeScore == null ? ''
    : (regime?.regime || (regimeScore >= 3.25 ? 'Risk-On' : regimeScore >= 1.75 ? 'Neutral' : 'Risk-Off'));
  const regimeTone = regimeLabel === 'Risk-On' ? 'up' : regimeLabel === 'Neutral' ? 'neutral' : regimeLabel ? 'down' : '';

  // Auth-aware behavior: the dashboard is now the homepage for logged-OUT ad
  // traffic too, so gate the logged-in-only controls and route anonymous
  // interactions to signup / the public screener / the tour.
  const authed = isAuthenticated();
  const modelAccess = useModelAccess();
  const visiblePages = pages.filter((p) => !p.requiresModelAccess || modelAccess);
  const openAuth = (mode) =>
    window.dispatchEvent(new CustomEvent('alphagrade:show-auth', { detail: { mode } }));
  // Open to anonymous visitors (matches docs/gating-map.md): browsable,
  // non-personalized surfaces whose endpoints need no auth. Cold ad traffic
  // that finds only one unlocked card reads the whole product as walled, and
  // never reaches a value moment worth signing up for.
  // The shared public-route list (LoginGate) plus the public screener: a
  // fourth hand-kept copy of this list lived here.
  const OPEN_PATHS = ['/screener', ...OPEN_APP_ROUTES];
  const handleCardClick = (page) => {
    if (authed) { navigate(page.path); return; }
    // Full page load, not client-side nav, so LoginGate decides what an
    // anonymous visitor sees for that route.
    if (OPEN_PATHS.includes(page.path)) { window.location.href = page.path; return; }
    openAuth('register');
  };
  // In-app links: authed users navigate; anonymous users get the signup prompt
  // (keeps logged-out visitors from client-side-entering the app tree, so they
  // never see the logged-in navigation).
  const navA = (path) => {
    if (authed) { navigate(path); return; }
    if (OPEN_PATHS.includes(path)) { window.location.href = path; return; }
    openAuth('register');
  };

  // Rendered in one of two places depending on auth (see the hero comment), so
  // it lives here rather than being duplicated in both branches.
  const marketPulse = (
    <div className="home-pulse">
      {trendsLoading ? (
        [...Array(4)].map((_, i) => <span key={i} className="home-pulse-item skeleton" />)
      ) : indices.length > 0 ? (
        indices.map(([key, d]) => (
          <span key={key} className="home-pulse-item">
            <span className="home-pulse-name">{key.toUpperCase()}</span>
            <span className="home-pulse-value">{fmtVal(d.value)}</span>
            <span className={`home-pulse-change ${d.changePercent >= 0 ? 'up' : 'down'}`}>
              {fmtPct(d.changePercent)}
            </span>
          </span>
        ))
      ) : null}
    </div>
  );

  const anonBtn = {
    background: 'none',
    border: '1px solid var(--border-medium, #334155)',
    color: 'var(--text-primary, #F1F5F9)',
    borderRadius: 8,
    padding: '0.4rem 0.8rem',
    fontWeight: 600,
    fontSize: '0.82rem',
    cursor: 'pointer',
  };

  return (
    <div className={`landing-page home-page${authed ? ' is-authed' : ''}`}>
      {/* Top-right controls */}
      <div className={`landing-top-controls${!authed ? ' anon' : ''}`}>
        {!authed && (
          <div className="landing-anon-actions" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginRight: '0.5rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <button style={anonBtn} onClick={() => window.dispatchEvent(new CustomEvent('alphagrade:open-tour'))}>
              Take a tour
            </button>
            <button style={anonBtn} onClick={() => openAuth('login')}>Sign in</button>
            <button
              style={{ ...anonBtn, background: 'var(--neon-green, #00D4AA)', border: 'none', color: '#04121a', fontWeight: 700 }}
              onClick={() => openAuth('register')}
            >
              Get started free
            </button>
          </div>
        )}
        {/* Settings gear */}
        <div className="landing-settings-wrapper" ref={menuRef} onKeyDown={(e) => { if (e.key === 'Escape') setSettingsOpen(false); }}>
          <button
            className="landing-settings-btn"
            onClick={() => setSettingsOpen(!settingsOpen)}
            title="Settings"
            aria-expanded={settingsOpen}
            aria-haspopup="true"
            aria-label="Settings"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
            </svg>
          </button>
          {settingsOpen && (
            <div className="landing-settings-menu">
              <div className="settings-section">
                <span className="settings-label">Color Scheme</span>
                <div className="scheme-options">
                  {COLOR_SCHEMES.map((scheme) => (
                    <button
                      key={scheme}
                      className={`scheme-btn ${colorScheme === scheme ? 'active' : ''}`}
                      onClick={() => setColorScheme(scheme)}
                      title={SCHEME_LABELS[scheme]}
                    >
                      <span className="scheme-swatch" style={{ background: `linear-gradient(135deg, ${SCHEME_COLORS[scheme][0]}, ${SCHEME_COLORS[scheme][1]})` }} />
                      <span className="scheme-name">{SCHEME_LABELS[scheme]}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="settings-section">
                <span className="settings-label">Appearance</span>
                <button className="settings-option" onClick={toggleTheme}>
                  <span>{theme === 'dark' ? '☀️' : '🌙'}</span>
                  <span>{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
                </button>
              </div>

              {authed && (<>
              {/* Resources */}
              <div className="settings-divider" />
              <div className="settings-section">
                <span className="settings-label">Resources</span>
                <button className="settings-option" onClick={() => { navigate('/faq'); setSettingsOpen(false); }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                    <line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                  <span>Help & FAQ</span>
                </button>
                <button className="settings-option" onClick={() => { navigate('/how-it-works'); setSettingsOpen(false); }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="20" x2="18" y2="10"/>
                    <line x1="12" y1="20" x2="12" y2="4"/>
                    <line x1="6" y1="20" x2="6" y2="14"/>
                  </svg>
                  <span>How Scores Work</span>
                </button>
                <button className="settings-option" onClick={() => { navigate('/glossary'); setSettingsOpen(false); }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                  </svg>
                  <span>Glossary</span>
                </button>
                <button className="settings-option" onClick={() => { navigate('/contact'); setSettingsOpen(false); }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                  <span>Contact / Feedback</span>
                </button>
              </div>
              </>)}

              {/* Alpha (Premium only) */}
              {hasFeature && hasFeature('alpha_memory') && (
                <>
                  <div className="settings-divider" />
                  <div className="settings-section">
                    <span className="settings-label">Alpha</span>
                    <button className="settings-option" onClick={() => { navigate('/alpha'); setSettingsOpen(false); }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                        <line x1="9" y1="10" x2="9" y2="10"/>
                        <line x1="12" y1="10" x2="12" y2="10"/>
                        <line x1="15" y1="10" x2="15" y2="10"/>
                      </svg>
                      <span>Alpha Workspace</span>
                    </button>
                    <button className="settings-option" onClick={() => { navigate('/alpha/memory'); setSettingsOpen(false); }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="9"/>
                        <path d="M12 7v5l3 3"/>
                      </svg>
                      <span>What Alpha Remembers</span>
                    </button>
                  </div>
                </>
              )}

              {authed && (<>
              {/* Subscription */}
              <div className="settings-divider" />
              <div className="settings-section">
                <span className="settings-label">Subscription</span>
                <button className="settings-option" onClick={() => { navigate('/pricing'); setSettingsOpen(false); }}>
                  <span>⭐</span>
                  <span>Your Plan: <strong style={{ color: tier === 'free' ? '#94A3B8' : '#6495ED' }}>{tierName}</strong></span>
                </button>
                {tier !== 'free' && (
                  <button className="settings-option" onClick={async () => {
                    setSettingsOpen(false);
                    try {
                      const token = localStorage.getItem(TOKEN_KEY);
                      const resp = await fetch('/api/subscription/create-portal', {
                        method: 'POST',
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      if (resp.ok) {
                        const data = await resp.json();
                        window.location.href = data.portal_url;
                      }
                    } catch {}
                  }}>
                    <span>💳</span>
                    <span>Manage Billing</span>
                  </button>
                )}
              </div>

              <div className="settings-divider" />
              <button className="settings-option" onClick={() => { setShowAccountModal(true); setSettingsOpen(false); setAccountError(null); setAccountSuccess(null); }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
                <span>Account Settings</span>
              </button>
              <button className="settings-option sign-out-option" onClick={signOut}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
                <span>Sign Out</span>
              </button>
              </>)}
            </div>
          )}
        </div>
      </div>

      {/* Background grid decoration */}
      <div className="landing-grid-bg" />

      {/* ── Slim hero: brand, and for signed-in users the live market pulse ──
          For anonymous visitors the pulse moves BELOW the grader (rendered
          further down). On a phone the anon top controls, the brand, and four
          index readings stacked up to push the ticker box off the first screen,
          so the one thing a cold visitor came to do was the one thing they
          couldn't see. Signed-in users still get the pulse first: they already
          know what the product does and want the tape. */}
      <header className="home-hero">
        <div className="home-hero-brand">
          <span className="home-logo">AG</span>
          <span className="home-wordmark"><span className="landing-highlight">Alpha</span>Grade</span>
        </div>
        {authed && marketPulse}
      </header>

      {/* ── Logged-out: grade a ticker right here, above everything else ──
          This is where paid traffic lands. Previously the first instruction a
          cold visitor got was "tap any card", which is a navigation decision,
          not a value moment — they had to get four clicks deep (home →
          screener → type → submit) before the product did anything for them.
          The widget is also the only place trackGrade() fires, so with it off
          this page the X conversion event had no way to record. */}
      {!authed && (
        <section className="home-freegrade">
          <h1 className="home-freegrade-h">
            Grade any stock in <span className="landing-highlight">seconds</span>
          </h1>
          <p className="home-freegrade-sub">
            34 tests across quality, setup, convexity, and the macro backdrop, with the
            work shown. No account, no card.
          </p>
          <HeroGradeWidget variant="panel" onGetStarted={() => openAuth('register')} />
        </section>
      )}

      {/* The pulse, for anonymous visitors, now that the grader has had the
          first screen. */}
      {!authed && <div className="home-pulse-standalone">{marketPulse}</div>}

      <div style={{ maxWidth: '1080px', width: '100%', margin: '0 auto 1.25rem', padding: '0 1rem' }}>
        <TreasuryYields />
      </div>

      {/* ── First-visit orientation ── */}
      {showIntro && (
        <div className="home-intro">
          <div className="home-intro-text">
            <strong>Welcome to AlphaGrade.</strong> Institutional-grade research and an AI thinking
            partner for investors who manage their own money. {authed ? 'Skim the market below, or jump straight into the ' : 'Skim the market below, or run a whole list at once in the '}
            <button className="home-intro-link" onClick={() => (authed ? navigate('/screener') : (window.location.href = '/screener'))}>Screener</button>{authed ? ' to score any stock.' : '.'}
          </div>
          <button className="home-intro-dismiss" onClick={dismissIntro} aria-label="Dismiss welcome">Got it</button>
        </div>
      )}

      {/* ── Explore your tools (kept high so navigation is above the fold) ── */}
      <section className="home-tools-section">
        <div className="home-section-head"><h2>{authed ? 'Explore your tools' : 'Go deeper — pick a tool'}</h2></div>
        {!authed && (
          <p style={{ margin: '-0.3rem 0 0.85rem', fontSize: '0.85rem', color: 'var(--text-secondary, #94A3B8)' }}>
            Tap any card. <strong style={{ color: '#00D4AA' }}>Free</strong> ones need no account —
            grade any stock, browse market trends, read the macro regime. 🔒 unlock with a free account.
          </p>
        )}
        <div className="home-tools">
          {visiblePages.map((page) => (
            <button
              key={page.path}
              className={`home-tool ${page.accent}`}
              onClick={() => handleCardClick(page)}
            >
              <span className="home-tool-icon">{page.icon}</span>
              <span className="home-tool-body">
                <span className="home-tool-label">
                  {page.title}
                  {page.badge && <span className="home-tool-badge">{page.badge}</span>}
                  {!authed && OPEN_PATHS.includes(page.path) && (
                    <span style={{ marginLeft: 6, fontSize: '0.6rem', fontWeight: 700, color: '#00D4AA', background: 'rgba(0,212,170,0.12)', border: '1px solid rgba(0,212,170,0.3)', borderRadius: 999, padding: '0.05rem 0.45rem', verticalAlign: 'middle' }}>Free</span>
                  )}
                  {!authed && !OPEN_PATHS.includes(page.path) && (
                    <span style={{ marginLeft: 6, fontSize: '0.7rem', verticalAlign: 'middle' }} title="Sign up free to unlock">🔒</span>
                  )}
                </span>
                <span className="home-tool-desc">{page.description}</span>
              </span>
              <span className="home-tool-arrow">&rarr;</span>
            </button>
          ))}
        </div>
      </section>

      {/* ── Proprietary: Market Regime (Energy Matrix) — the differentiator ── */}
      <section className="home-card home-regime">
        <div className="home-section-head">
          <h2>Market Regime</h2>
          <button className="home-link" onClick={() => navA('/energy')}>Macro Energy &rarr;</button>
        </div>
        {regime ? (
          <div className="regime-body">
            <div className={`regime-headline ${regimeTone}`}>
              <span className="regime-label">{regimeLabel}</span>
              <span className="regime-score">{regimeScore.toFixed(1)}<span className="regime-score-max">/5</span></span>
              <span className="regime-tag">Energy Matrix</span>
            </div>
            <div className="regime-inputs">
              {REGIME_INPUTS.map(({ key, label }) => (
                <div key={key} className="regime-input">
                  <span className="regime-input-label">{label}</span>
                  <div className="regime-bar">
                    <div className="regime-bar-fill" style={{ width: `${Math.round((regime[key] || 0) * 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
            <p className="home-muted regime-note">
              AlphaGrade's read on whether conditions favor risk — the same Energy Matrix that
              tunes your conviction scores. You won't find this on a quote page.
            </p>
          </div>
        ) : regimeLoading ? (
          <div className="regime-body"><span className="skeleton" style={{ display: 'block', width: '180px', height: '44px' }} /></div>
        ) : (
          <p className="home-muted">Regime data is unavailable right now.</p>
        )}
      </section>

      {/* ── Proprietary carrot: Today's Highest-Conviction Names ── */}
      {spotlight?.available && (
        <section className="home-card home-spotlight">
          <div className="home-section-head">
            <h2>Today's Highest-Conviction Names</h2>
            {spotlight.as_of && <span className="spotlight-asof">{spotlight.as_of}</span>}
          </div>
          <p className="home-muted spotlight-tag">
            {spotlight.total_high > 0 ? `${spotlight.total_high} names scored Promising or better today, ` : 'Today’s highest-conviction names, '}graded live
            across quality, setup &amp; convexity. {spotlight.unlocked ? 'Your full list:' : ''}
          </p>
          <div className="spotlight-list">
            {spotlight.names.map((n, i) => (
              <div key={n.symbol} className="spotlight-row">
                <span className="spotlight-rank">#{i + 1}</span>
                <span className="spotlight-sym">{n.symbol}</span>
                <span className="spotlight-sector">{n.sector}</span>
                {n.trend === 'new' && <span className="spotlight-move move-new">NEW</span>}
                {n.trend === 'up' && n.delta != null && <span className="spotlight-move move-up">▲ +{n.delta}</span>}
                {n.trend === 'down' && n.delta != null && <span className="spotlight-move move-down">▼ {n.delta}</span>}
                <span className="spotlight-score">{n.conviction}<small>/100</small></span>
                <span className="spotlight-sub">Q {n.quality} · S {n.setup} · CX {n.convex}</span>
              </div>
            ))}
            {!spotlight.unlocked && [...Array(spotlight.locked_count)].map((_, i) => (
              <div key={`lock-${i}`} className="spotlight-row locked">
                <span className="spotlight-rank">#{i + 2}</span>
                <span className="spotlight-lockbar" />
                <span className="spotlight-lock" aria-hidden="true">🔒</span>
              </div>
            ))}
          </div>
          {!spotlight.unlocked && (
            <div className="spotlight-cta">
              <div className="spotlight-cta-text">
                <strong>{spotlight.locked_count} more high-conviction names</strong> behind the lock —
                unlock the full list and every score.
                {spotlight.sectors && Object.keys(spotlight.sectors).length > 0 && (
                  <span className="spotlight-sectors">
                    {' '}Today spans {Object.entries(spotlight.sectors).map(([s, n]) => `${s} (${n})`).join(', ')}.
                  </span>
                )}
              </div>
              <button className="spotlight-unlock-btn" onClick={() => navA('/pricing')}>Unlock &rarr;</button>
            </div>
          )}
        </section>
      )}

      {/* ── Market content: movers + sentiment + sectors (left) | heatmap (right) ── */}
      <main className="home-main">
        <div className="home-left-col">
          <section className="home-card home-movers">
            <div className="home-section-head">
              <h2>What's moving today</h2>
              <button className="home-link" onClick={() => navA('/trends')}>Market Trends &rarr;</button>
            </div>
            {trendsLoading ? (
              <div className="home-movers-cols">
                {[0, 1].map((c) => (
                  <div key={c} className="home-movers-col">
                    {[...Array(8)].map((_, i) => <span key={i} className="home-mover-row skeleton" />)}
                  </div>
                ))}
              </div>
            ) : trendsError || (gainers.length === 0 && losers.length === 0) ? (
              <p className="home-muted">Live market data is unavailable right now.</p>
            ) : (
              <div className="home-movers-cols">
                <div className="home-movers-col">
                  <span className="home-movers-label up">Gainers</span>
                  {gainers.map((s) => (
                    <button key={s.symbol} className="home-mover-row" onClick={() => navA('/trends')} title={s.name}>
                      <span className="home-mover-sym">{s.symbol}</span>
                      <span className="home-mover-chg up">+{fmtVal(s.change)}%</span>
                    </button>
                  ))}
                </div>
                <div className="home-movers-col">
                  <span className="home-movers-label down">Losers</span>
                  {losers.map((s) => (
                    <button key={s.symbol} className="home-mover-row" onClick={() => navA('/trends')} title={s.name}>
                      <span className="home-mover-sym">{s.symbol}</span>
                      <span className="home-mover-chg down">{fmtVal(s.change)}%</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </section>

          <section className="home-card">
            <div className="home-section-head"><h2>Market Sentiment</h2></div>
            {fearGreed ? (
              <div className="fg-block">
                <div className="fg-gauge">
                  <div
                    className="fg-gauge-fill"
                    style={{ width: `${fearGreed.value}%`, background: fearGreed.value > 50 ? '#10B981' : '#EF4444' }}
                  />
                </div>
                <div className="fg-meta">
                  <span className="fg-num">{fearGreed.value}</span>
                  <span className="fg-lbl">{fearGreed.label}</span>
                </div>
              </div>
            ) : (
              <p className="home-muted">Sentiment data is unavailable right now.</p>
            )}
          </section>

          <section className="home-card">
            <div className="home-section-head"><h2>Sector Performance</h2></div>
            {sectors.length > 0 ? (
              <div className="sector-chips">
                {sectors.map((s) => (
                  <span key={s.name} className={`sector-chip ${s.isUp ? 'up' : 'down'}`}>
                    {s.name} <b>{fmtPct(s.change)}</b>
                  </span>
                ))}
              </div>
            ) : (
              <p className="home-muted">Sector data is unavailable right now.</p>
            )}
          </section>
        </div>

        <section className="home-card home-heatmap">
          <div className="home-section-head">
            <h2>Market Map</h2>
          </div>
          <MarketHeatmap />
        </section>
      </main>

      {/* ── Latest news (with thumbnails) ── */}
      <section className="home-card home-news">
        <div className="home-section-head">
          <h2>Latest market news</h2>
          <button className="home-link" onClick={() => navA('/trends')}>More on Market Trends &rarr;</button>
        </div>
        {newsLoading ? (
          <div className="news-grid">
            {[...Array(6)].map((_, i) => <span key={i} className="news-item skeleton" style={{ height: '88px' }} />)}
          </div>
        ) : news.length > 0 ? (
          <div className="news-grid">
            {news.map((a, i) => (
              <a key={i} className="news-item" href={a.url} target="_blank" rel="noopener noreferrer">
                <NewsThumb src={a.image} />
                <span className="news-body">
                  <span className="news-title">{a.title}</span>
                  <span className="news-meta">
                    <span className={`news-dot ${a.sentiment}`} />
                    {a.source} · {a.publishedDate}
                  </span>
                </span>
              </a>
            ))}
          </div>
        ) : (
          <p className="home-muted">News is unavailable right now.</p>
        )}
      </section>

      {/* Account Settings Modal */}
      {showAccountModal && (
        <div className="account-modal-overlay" onClick={() => setShowAccountModal(false)}>
          <div className="account-modal" onClick={(e) => e.stopPropagation()}>
            <div className="account-modal-header">
              <h3>Account Settings</h3>
              <button className="account-modal-close" onClick={() => setShowAccountModal(false)}>&times;</button>
            </div>
            <div className="account-current-email">
              Signed in as <strong>{userEmail || '...'}</strong>
            </div>
            <div className="account-tabs">
              <button className={`account-tab ${accountTab === 'email' ? 'active' : ''}`} onClick={() => { setAccountTab('email'); setAccountError(null); setAccountSuccess(null); }}>Change Email</button>
              <button className={`account-tab ${accountTab === 'password' ? 'active' : ''}`} onClick={() => { setAccountTab('password'); setAccountError(null); setAccountSuccess(null); }}>Change Password</button>
              <button className={`account-tab account-tab-danger ${accountTab === 'delete' ? 'active' : ''}`} onClick={() => { setAccountTab('delete'); setAccountError(null); setAccountSuccess(null); setDeleteConfirmed(false); }}>Delete Account</button>
            </div>
            <form className="account-form" onSubmit={handleAccountSubmit}>
              {accountTab === 'email' ? (
                <>
                  <div className="account-field">
                    <label htmlFor="land-new-email">New Email</label>
                    <input id="land-new-email" type="email" autoComplete="off" value={accountForm.newEmail} onChange={(e) => setAccountForm(f => ({ ...f, newEmail: e.target.value }))} placeholder="new@example.com" required disabled={accountSubmitting} />
                  </div>
                  <div className="account-field">
                    <label htmlFor="land-password">Current Password</label>
                    <input id="land-password" type="password" autoComplete="new-password" value={accountForm.password} onChange={(e) => setAccountForm(f => ({ ...f, password: e.target.value }))} placeholder="Enter current password" required disabled={accountSubmitting} />
                  </div>
                </>
              ) : accountTab === 'password' ? (
                <>
                  <div className="account-field">
                    <label htmlFor="land-cur-pw">Current Password</label>
                    <input id="land-cur-pw" type="password" autoComplete="new-password" value={accountForm.currentPassword} onChange={(e) => setAccountForm(f => ({ ...f, currentPassword: e.target.value }))} placeholder="Enter current password" required disabled={accountSubmitting} />
                  </div>
                  <div className="account-field">
                    <label htmlFor="land-new-pw">New Password</label>
                    <input id="land-new-pw" type="password" autoComplete="new-password" value={accountForm.newPassword} onChange={(e) => setAccountForm(f => ({ ...f, newPassword: e.target.value }))} placeholder="Enter new password" required disabled={accountSubmitting} />
                  </div>
                  <div className="account-field">
                    <label htmlFor="land-confirm-pw">Confirm New Password</label>
                    <input id="land-confirm-pw" type="password" autoComplete="new-password" value={accountForm.confirmNewPassword} onChange={(e) => setAccountForm(f => ({ ...f, confirmNewPassword: e.target.value }))} placeholder="Re-enter new password" required disabled={accountSubmitting} />
                  </div>
                </>
              ) : null}
              {accountTab === 'delete' && (
                <>
                  <div className="account-danger-note">
                    <strong>This permanently deletes your account.</strong> Your portfolios,
                    watchlists, Alpha history, and settings will be erased and cannot be
                    recovered. Any active subscription is cancelled automatically.
                  </div>
                  <div className="account-field">
                    <label htmlFor="land-del-pw">Confirm Password</label>
                    <input id="land-del-pw" type="password" autoComplete="new-password" value={accountForm.currentPassword} onChange={(e) => setAccountForm(f => ({ ...f, currentPassword: e.target.value }))} placeholder="Enter your password" required disabled={accountSubmitting} />
                  </div>
                  <label className="account-danger-check">
                    <input type="checkbox" checked={deleteConfirmed} onChange={(e) => setDeleteConfirmed(e.target.checked)} disabled={accountSubmitting} />
                    <span>I understand this is permanent and cannot be undone.</span>
                  </label>
                </>
              )}
              {accountError && <div className="account-error">{accountError}</div>}
              {accountSuccess && <div className="account-success">{accountSuccess}</div>}
              {accountTab === 'delete' ? (
                <button type="submit" className="account-submit-btn account-delete-btn" disabled={accountSubmitting || !deleteConfirmed || !accountForm.currentPassword}>
                  {accountSubmitting ? 'Deleting…' : 'Permanently delete my account'}
                </button>
              ) : (
                <button type="submit" className="account-submit-btn" disabled={accountSubmitting}>
                  {accountSubmitting ? 'Updating...' : accountTab === 'email' ? 'Update Email' : 'Update Password'}
                </button>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
